import { memo, useContext, useEffect, useRef } from 'react'
import { useIntl } from 'react-intl'
import { rem } from 'polished'
import styled from 'styled-components'

import LargeContentToggle from 'components/Common/LargeContentToggle'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import { useElementHeight } from 'hooks/useElementSize'
import useDidChange from 'hooks/useDidChange'
import useToggle from 'hooks/useToggle'
import BodyText from 'LuxKit/Typography/BodyText'
import OfferPageStateContext from 'contexts/OfferPage/offerPageStateContext'
import ExperiencesPassportOfferSummary from 'components/Experiences/ExperiencesPassport/ExperiencesPassportOfferSummary'
import { useAppSelector } from 'hooks/reduxHooks'
import getLoyaltyDisplayTier from 'loyalty/selectors/getLoyaltyDisplayTier'
import { tierDarkForegroundColorStyle } from 'styles/utils/tierStyles'
import type { ExperiencesPassportOfferSource } from 'components/Experiences/ExperiencesPassport/experiencesPassportTypes'

import PropertyRateInclusionsStack, { type PropertyRateInclusionListsProps } from './PropertyRateInclusionsStack'

const UpgradedInclusionsHeading = styled(BodyText)`
  ${tierDarkForegroundColorStyle}
`

/**
 * Cross-fade + slide between the base and target rate inclusions when the package upgrade toggles
 */
const InclusionsSwapContainer = styled.div`
  display: grid;
  grid-template-areas: 'base-rate' 'target-upgrade-rate';
  grid-template-rows: 1fr;
`

const BaseRateInclusions = styled.div`
  grid-area: base-rate;
  grid-row: 1;
  opacity: 1;
  transform: translateY(0);
  transition: opacity 300ms ease, transform 300ms ease;
  transition-delay: 300ms;

  &:not(.show) {
    opacity: 0;
    transform: translateY(${rem(16)});
    transition-delay: 0ms;
    pointer-events: none;
  }
`

const TargetUpgradeRateInclusions = styled.div`
  grid-area: target-upgrade-rate;
  grid-row: 1;
  opacity: 1;
  transform: translateY(0);
  transition: opacity 300ms ease, transform 300ms ease;
  transition-delay: 300ms;

  &:not(.show) {
    opacity: 0;
    transform: translateY(${rem(-16)});
    transition-delay: 0ms;
    pointer-events: none;
  }
`

interface Props extends PropertyRateInclusionListsProps {
  passportOffer?: ExperiencesPassportOfferSource
  maxHeight: number
  /**
   * Loyalty upgrades: when the rate has a free package-upgrade target, both rates' inclusions render
   * stacked and `upgradeApplied` cross-fades between them as the upgrade is toggled.
   */
  upgradeTargetRate?: App.EnrichedPropertyRate
  upgradeApplied?: boolean
}

function PropertyRateInclusions({
  property,
  inclusions,
  bonusInclusions,
  luxPlusInclusions,
  bundleInclusions,
  passportOffer,
  maxHeight,
  upgradeTargetRate,
  upgradeApplied,
}: Props) {
  const intl = useIntl()
  const displayTier = useAppSelector(getLoyaltyDisplayTier)
  const { flights, rooms, charterBundleRoute } = useContext(OfferPageStateContext)
  const showReturnFlightsInclusion = flights && !charterBundleRoute && !property.mustBundleWithFlights
  const contentRef = useRef<HTMLDivElement>(null)
  const inclusionsSwapContainerRef = useRef<HTMLDivElement>(null)
  const baseRateInclusionsRef = useRef<HTMLDivElement>(null)
  const targetUpgradeRateInclusionsRef = useRef<HTMLDivElement>(null)
  const contentHeight = useElementHeight(contentRef)
  const inclusionsSwapContainerHeight = useElementHeight(inclusionsSwapContainerRef)
  const baseRateInclusionsHeight = useElementHeight(baseRateInclusionsRef)
  const targetUpgradeRateInclusionsHeight = useElementHeight(targetUpgradeRateInclusionsRef)
  const upgradeAppliedChanged = useDidChange(upgradeApplied, upgradeApplied)
  const {
    value: inclusionsExpanded,
    toggle: toggleInclusionsExpanded,
    off: collapseInclusions,
  } = useToggle()
  const inclusionsOpen = inclusionsExpanded && !upgradeAppliedChanged

  useEffect(() => {
    if (upgradeAppliedChanged) {
      collapseInclusions()
    }
  }, [collapseInclusions, upgradeAppliedChanged])

  const activeInclusionsHeight = upgradeTargetRate
    ? (upgradeApplied ? targetUpgradeRateInclusionsHeight : baseRateInclusionsHeight) ?? undefined
    : undefined
  const activeContentHeight = activeInclusionsHeight !== undefined &&
    contentHeight !== null && inclusionsSwapContainerHeight !== null
    ? contentHeight - inclusionsSwapContainerHeight + activeInclusionsHeight
    : undefined

  const baseStack = <PropertyRateInclusionsStack
    property={property}
    inclusions={inclusions}
    bonusInclusions={bonusInclusions}
    luxPlusInclusions={luxPlusInclusions}
    bundleInclusions={bundleInclusions}
    showReturnFlightsInclusion={showReturnFlightsInclusion}
    rooms={rooms}
  />

  return <LargeContentToggle
    maxHeight={maxHeight}
    contentHeightOverride={activeContentHeight}
    open={inclusionsOpen}
    onToggle={toggleInclusionsExpanded}
    unconstrainedWhenExpanded={!!passportOffer}
    showMoreText={intl.formatMessage({
      id: '74uMot_LhpmF_fW845tr',
      defaultMessage: 'View more inclusions',
    })}
    showLessText={intl.formatMessage({
      id: 'i_MD93nsLRPDKVjXbxqE',
      defaultMessage: 'View less inclusions',
    })}
  >
    <VerticalSpacer ref={contentRef} gap={12}>
      {upgradeTargetRate && <InclusionsSwapContainer ref={inclusionsSwapContainerRef}>
        <BaseRateInclusions className={upgradeApplied ? undefined : 'show'}>
          <div ref={baseRateInclusionsRef}>
            {baseStack}
          </div>
        </BaseRateInclusions>
        <TargetUpgradeRateInclusions className={upgradeApplied ? 'show' : undefined}>
          <div ref={targetUpgradeRateInclusionsRef}>
            <VerticalSpacer gap={4}>
              <UpgradedInclusionsHeading variant="medium" weight="bold" className={`tier-${displayTier}`}>
                {intl.formatMessage({
                  id: 'HRYT9P5ypghRMebUdoEQ',
                  defaultMessage: 'Your upgraded inclusions',
                })}
              </UpgradedInclusionsHeading>
              <PropertyRateInclusionsStack
                property={property}
                inclusions={upgradeTargetRate.inclusions}
                bonusInclusions={upgradeTargetRate.bonusInclusions}
                luxPlusInclusions={upgradeTargetRate.luxPlusInclusions}
                bundleInclusions={upgradeTargetRate.bundleInclusions}
                showReturnFlightsInclusion={showReturnFlightsInclusion}
                rooms={rooms}
              />
            </VerticalSpacer>
          </div>
        </TargetUpgradeRateInclusions>
      </InclusionsSwapContainer>}
      {!upgradeTargetRate && baseStack}
      {passportOffer && <ExperiencesPassportOfferSummary offer={passportOffer} />}
    </VerticalSpacer>
  </LargeContentToggle>
}

export default memo(PropertyRateInclusions)
